import * as React from 'react';
import { FieldError as FormFieldError, useForm } from 'react-hook-form';
import { css } from 'styled-components';
import { getTotalPrice } from '../utils/getTotalPrice';
import { Layout } from './Layout';
import { styled } from './ThemeProvider';

type FormValues = {
  name: string;
  email: string;
  companyName: string;
  NIP: string;
  hours: string;
  message: string;
  price: number;
};

const messages = {
  requiredField: 'To pole jest wymagane.',
};

const Label = styled.label`
  font-size: .8em;
  display: block;
  color: ${({ theme }) => theme.colors.label};
  font-weight: 400;
  margin: 0;
  padding: .5em 0;
`;

const SubmitInput = styled.input`
  color: ${({ theme }) => theme.colors.primary};
  background-color: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.primary};

  border-radius: 10px;
  padding: 1em 2em;
  transition: all 0.1s ease-in-out;
  font-size: 16px;
  line-height: 1;
  margin: 0;
  position: relative;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: .02em;

  -webkit-appearance: button;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.primary};
    border: 2px solid ${({ theme }) => theme.colors.primary};
  }
`;

const inputStyle = ({ theme }) => css`
  font-family: inherit;
  color: ${({ theme }) => theme.colors.inputText};
  background-color: ${({ theme }) => theme.colors.white};
  font-weight: 700;
  padding: .75em 1em;
  transition: all 0.5s ease-in-out;
  outline: none;
  border: 1px solid ${({ theme }) => theme.colors.white};
  min-height: 40px;

  &:placeholder-shown {
    font-size: .8em;
    font-weight: 100;
  }

  ::-webkit-input-placeholder { /* Edge */
    color: ${({ theme }) => theme.colors.placeholder};
  }

  :-ms-input-placeholder { /* Internet Explorer 10-11 */
    color: ${({ theme }) => theme.colors.placeholder};
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.placeholder};
  }

  &:focus {
    box-shadow: 0 0 5px ${({ theme }) => theme.colors.primary};
    border: 1px solid ${({ theme }) => theme.colors.primary};
    outline: none;
  }
`;

const TextInput = styled.input`
  ${inputStyle};
`;

const MultilineTextInput = styled.textarea`
  ${inputStyle};
`;

const InputGroup = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: .5em;
  margin-bottom: .5em;
  padding-left: 1em;
  padding-right: 1em;
`;

const InputGroup2 = styled(InputGroup)`
  @media (${props => props.theme.deviceBreakpoints.tablet}) {
    width: 50%;
  }
`;

const ProductDescription = styled.div`

`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f6f6f6;
  padding: 2em;
`;

const Inputs = styled.div`
  flex: 1;
  display: flex;
  flex-flow: wrap;
  flex-direction: row;

  margin-left: -1em;
  margin-right: -1em;
`;

const FieldErrorContainer = styled.div`
  color: darkred;
  font-size: .8em;
  margin-top: .2em;
`;

const FieldError: React.FC<{ error?: FormFieldError }> = ({ error }) => {
  if (!error) {
    return null;
  }

  return (
    <FieldErrorContainer>
      {error.message}
    </FieldErrorContainer>
  );
};

const Price = styled.span`
  font-style: italic;
  font-weight: 700;
`;

const FormFooter = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  margin-top: 2em;
`;

type Props = {
  productId: string;
  initialPrice: number;
  productDescription?: string;
};

export const ProductPage: React.FC<Props> = ({
                                               productId,
                                               initialPrice,
                                               productDescription,
                                             }) => {

  const { handleSubmit, register, errors, watch } = useForm<FormValues>({
    defaultValues: {
      hours: '1',
      price: initialPrice,
    },
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  return (
    <Layout
      title=""
    >
      <Form
        onSubmit={handleSubmit((values) => {
          const totalGrossPrice = getTotalPrice(values.hours, values.price);
          document.location.href =
            `/api/create-order?produkt=${productId}&kwota=${totalGrossPrice * 100}&email=${values.email}&name=${values.name}&companyName=${values.companyName}&NIP=${values.NIP}&message=${values.message}`;
        })}
      >
        <ProductDescription dangerouslySetInnerHTML={{ __html: productDescription }}/>
        <Inputs>
          <InputGroup2>
            <Label htmlFor="name">Imie i nazwisko</Label>
            <TextInput
              placeholder={'Imię i nazwisko'}
              type="text"
              name="name"
              id="name"
              ref={register({
                required: 'Potrzebujemy tej informacji.',
              })}
            />
            <FieldError error={errors.name}/>
          </InputGroup2>
          <InputGroup2>
            <Label htmlFor="email">Email</Label>
            <TextInput
              placeholder={'email@example.com'}
              type="text"
              name="email"
              id="email"
              ref={register({
                required: 'Potrzebujemy e-mail aby umówić termin konsultacji.',
              })}
            />
            <FieldError error={errors.email}/>
          </InputGroup2>
          <InputGroup2>
            <Label htmlFor="companyName">Nazwa firmy</Label>
            <TextInput
              type="text"
              name="companyName"
              id="companyName"
              ref={register()}
            />
            <FieldError error={errors.companyName}/>
          </InputGroup2>
          <InputGroup2>
            <Label htmlFor="NIP">NIP</Label>
            <TextInput
              type="text"
              name="NIP"
              id="NIP"
              ref={register()}
            />
            <FieldError error={errors.NIP}/>
          </InputGroup2>
          <InputGroup2>
            <Label htmlFor="price">Wartość (zł)</Label>
            <TextInput
              type="number"
              name="price"
              id="price"
              defaultValue={initialPrice}
              ref={register({
                min: {
                  value: 0,
                  message: 'Wartość musi być większa niż 0',
                },
                required: messages.requiredField,
              })}
            />
            <FieldError error={errors.hours}/>
          </InputGroup2>
          <InputGroup2>
            <Label htmlFor="hours">Liczba godzin</Label>
            <TextInput
              type="number"
              name="hours"
              id="hours"
              defaultValue={1}
              ref={register({
                min: {
                  value: 1,
                  message: 'Zamówienie musi mieć przynajmniej jedną godzinę.',
                },
                required: messages.requiredField,
              })}
            />
            <FieldError error={errors.hours}/>
          </InputGroup2>
          <InputGroup>
            <Label htmlFor="message">Komentarz</Label>
            <MultilineTextInput
              rows={2}
              name="message"
              id="message"
              ref={register()}
            />
            <FieldError error={errors.message}/>
          </InputGroup>
        </Inputs>
        <FormFooter>
          <Price>
            {`Cena: ${(getTotalPrice(watch('hours'), watch('price')))} zł (kwota netto + 23% VAT)`}
          </Price>
          <SubmitInput
            css={`
              margin: 1em;
            `}
            type="submit"
            value="ZAMAWIAM"
          />
        </FormFooter>
      </Form>
    </Layout>
  );
};
